/**
 * The session, on the client.
 *
 * ## What is stored, and what is deliberately not
 *
 * ONE opaque token. That is all. No participant id, no tier, no "isLoggedIn" flag.
 *
 * Every one of those would be a lie waiting to happen: a tier cached at sign-in is stale the moment
 * a compliance officer changes it, and a UI that renders from a cached `tier` will happily show a
 * user a screen the backend is about to refuse. The token is the ONLY thing the client is entitled
 * to hold, because it is the only thing the client cannot make up. Everything else comes from
 * `/auth/me`, on every boot, from the server that actually knows.
 *
 * ## Why localStorage, and what that costs
 *
 * A token in localStorage is readable by any script that runs on this origin, which means an XSS
 * becomes a session theft. The alternative — an httpOnly cookie the JS cannot touch — is genuinely
 * stronger against XSS and brings CSRF along with it, which then needs its own defence.
 *
 * We take localStorage for now, with eyes open, because the mitigation that actually matters is
 * already in place on the server: sessions are REVOCABLE (ADR 0006). A stolen token can be killed
 * from the user's own settings screen, and a PIN change kills every session on the account. A stolen
 * JWT could not have been.
 *
 * Moving to httpOnly cookies is a backend change (set the cookie at login, read it in `_bearer`) and
 * a small one. It is worth doing before the pilot. It is written down here rather than left as folk
 * knowledge, because "we meant to" is not a control.
 */

const TOKEN_KEY = 'lipa.session.token';

/** In-memory first, so a server-side render or a locked-down browser does not crash. */
let inMemory: string | null = null;

export function getToken(): string | null {
  // UI-ONLY MODE: hand back a stub so the app boots straight into a signed-in session with the
  // backend off. A no-op unless NEXT_PUBLIC_UI_MOCKS=1. See lib/mocks.ts.
  if (import.meta.env['VITE_UI_MOCKS'] === '1') return 'mock-token';

  if (inMemory) return inMemory;
  if (typeof window === 'undefined') return null;

  try {
    inMemory = window.localStorage.getItem(TOKEN_KEY);
    return inMemory;
  } catch {
    // Private mode, or storage disabled. The user can still use the app for this one session.
    return null;
  }
}

export function setToken(token: string): void {
  inMemory = token;
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* memory-only session; better than refusing to sign in */
  }
}

export function clearToken(): void {
  inMemory = null;
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to clear */
  }
}
